/* Bokja header state — v9.2 (2026-08-25). One speed down; one rigid sheet back; the
 * shown strip is a pin, not a translate.
 *
 * DOWN: no JavaScript. The header rides off with the page (park pin just above the
 * viewport); the strip pins at top 0; birds arrive on the collapsed class. Approved and
 * locked ("way down is back to being great").
 *
 * BACK: the nav and the strip return as one rigid sheet — both pre-positioned so the
 * nav's bottom edge sits exactly on the strip's top edge, both travelling the SAME
 * var-derived distance on the same 300ms clock (v9.1: rigidity verified frame-by-frame).
 * Shown positions are STICKY PINS (nav at -announce, strip below the nav), so once the
 * ride settles the geometry is correct at every scroll position and the top handoff is
 * native — v9.1 expressed the strip via a gated translate, and the gate dropping at
 * y≈165 made the filters vanish behind the nav until y≈40 (James: "we still lose parts
 * of the menu, at different stages"). Pin swaps happen only between visually identical
 * frames, wrapped in transition-none; all visible motion is inline compositor transform.
 *
 * Intent: show after 50px of upward travel from the reversal point, only while genuinely
 * parked (deeper than the park depth); hide after 8px of downward travel, ALSO only
 * while deeper than the park depth — reversing down inside the near-top zone keeps the
 * sheet until you are genuinely deep again, which deletes every zone-handoff edge case.
 * At y ≤ announce-height everything drops silently where pins and flow coincide.
 * scrollY is clamped so rubber-band bounce cannot fake reversals; settle wobble can
 * neither summon nor dismiss.
 */
(function () {
  var group = document.getElementById('header-group');
  if (!group) return;

  var root = document.documentElement;

  var strip = document.querySelector('.shopify-section:has(> .bokja-category-nav.is-sticky)')
           || document.querySelector('.bokja-category-nav') && document.querySelector('.bokja-category-nav').closest('.shopify-section');

  var markImg = document.querySelector('header.bokja-nav .bokja-nav__mark');
  if (markImg && (markImg.currentSrc || markImg.src)) {
    root.style.setProperty('--bokja-birds-url', 'url("' + (markImg.currentSrc || markImg.src) + '")');
  }

  var COLLAPSED = 'bokja-header-collapsed';
  var RETURNED = 'bokja-header-returned';
  var SHEET = 'bokja-header-sheet';
  var ANIM = 'bokja-header-anim';

  var SHOW_TRAVEL = 50;
  var HIDE_TRAVEL = 8;
  var PARK_BUFFER = 60;   // must match the CSS park pin: -(header-height + 60px)

  var state = { collapsed: false, returned: false, sheet: false, anim: false };
  function toggle(key, cls, on) {
    if (state[key] !== on) { state[key] = on; root.classList.toggle(cls, on); }
  }

  function clampY() {
    var max = Math.max(document.documentElement.scrollHeight - window.innerHeight, 0);
    return Math.min(Math.max(window.scrollY, 0), max);
  }
  function varPx(name, fallback) {
    var v = parseFloat(getComputedStyle(root).getPropertyValue(name));
    return isNaN(v) ? fallback : v;
  }
  function slideDistance() {
    /* v10: the sheet is nav + strip and enters fully from above (the strip no longer
       waits pinned at the top), so the travel is the whole sheet's height: nav
       (header minus announcement) plus the strip. Shared by bar and strip — rigid. */
    /* v11: the strip pins at the top again while collapsed, so the sheet ride reverts to
       the v9 geometry — the bar travels nav-height and the strip rides from its pinned
       line to under the nav, flush by shared transform. */
    return varPx('--bokja-header-height', group.offsetHeight) - varPx('--bokja-announce-height', 40);
  }

  function setTransforms(px, instant) {
    var v = px === 0 ? 'translateY(0px)' : 'translateY(' + px + 'px)';
    if (instant) { group.style.transition = 'none'; if (strip) strip.style.transition = 'none'; }
    group.style.transform = v;
    if (strip) strip.style.transform = v;
    if (instant) {
      void group.offsetHeight;
      group.style.transition = '';
      if (strip) strip.style.transition = '';
    }
  }
  function clearTransforms(instant) {
    if (instant) { group.style.transition = 'none'; if (strip) strip.style.transition = 'none'; }
    group.style.transform = '';
    if (strip) strip.style.transform = '';  /* always clear — harmless when it never rode */
    if (instant) {
      void group.offsetHeight;
      group.style.transition = '';
      if (strip) strip.style.transition = '';
    }
  }

  var lastY = clampY();
  var goingUp = false;
  var upReversalY = lastY;
  var downReversalY = lastY;
  var wantShown = false;
  var settleTimer = null;
  var ticking = false;
  var activeHead = null;

  /* v9.4: the pinned centre title rides the SAME compositor choreography as the sheet.
     Its `top` pin flips instantly with the classes; we measure its rendered position
     before and after the flip and bridge the difference with an inline transform on the
     shared clock — so it can never shear against the strip the way a main-thread `top`
     transition did (the "small gap where we see the photos" on device). */
  function pinnedHeadEl() {
    var heads = document.querySelectorAll('.bokja-catgroup__head, .bokja-storygroup__head');
    var best = null, bestTop = 300;
    for (var i = 0; i < heads.length; i++) {
      var r = heads[i].getBoundingClientRect();
      if (r.height > 0 && r.top < bestTop) { best = heads[i]; bestTop = r.top; }
    }
    return best;
  }
  function prepHead() {
    var el = pinnedHeadEl();
    return el ? { el: el, top: el.getBoundingClientRect().top } : null;
  }
  function bridgeHead(p) {
    if (activeHead && (!p || p.el !== activeHead)) clearHead();
    if (!p) return;
    var el = p.el;
    el.style.transition = 'none';
    el.style.transform = '';
    var delta = p.top - el.getBoundingClientRect().top;
    if (Math.abs(delta) < 1) { el.style.transition = ''; activeHead = null; return; }
    el.style.transform = 'translateY(' + delta + 'px)';
    void el.offsetHeight;
    el.style.transition = 'transform 300ms ease-out';
    el.style.transform = 'translateY(0px)';
    activeHead = el;
  }
  function clearHead() {
    if (!activeHead) return;
    activeHead.style.transition = 'none';
    activeHead.style.transform = '';
    void activeHead.offsetHeight;
    activeHead.style.transition = '';
    activeHead = null;
  }
  /* v11.2: the head PIN flips with the classes IN BOTH DIRECTIONS (the CSS keys it on
     sheet:not(collapsed), and beginHide sets collapsed in the same turn), so bridgeHead's
     delta→0 ease is the correct ride for BOTH flips — v11.1 misread the hide as "pin
     stays shown" and animated the title to −slide on top of the already-flipped pin,
     overshooting it ~a slide-height above the line and then sailing it back down at
     settle (James, 2026-08-25: "goes up and then it comes down again with the white
     line"). What v11.1 got RIGHT and this keeps: the mid-ride HANDOFF. Fast scroll
     across group boundaries swaps WHICH head is sticky-pinned while a ride is in
     flight; the newcomer pins straight to settled geometry with no JS event — the
     measured 90px hole (71/344 flurry frames). handoffHead() glues the newcomer to the
     riding sheet: its start offset is the sheet's live compositor transform,
     re-expressed against the collapsed pin on hides (+slide), easing to its pin at the
     sheet's own pace. */
  function sheetOffsetPx() {
    var t = getComputedStyle(group).transform;
    if (!t || t === 'none') return 0;
    var m = t.match(/matrix\(([^)]+)\)/);
    return m ? (parseFloat(m[1].split(',')[5]) || 0) : 0;
  }
  function handoffHead() {
    var el = pinnedHeadEl();
    if (!el || el === activeHead) return;
    if (activeHead) clearHead();
    var from = sheetOffsetPx() + (wantShown ? 0 : slideDistance());
    el.style.transition = 'none';
    el.style.transform = '';
    void el.offsetHeight;
    if (Math.abs(from) < 1) { el.style.transition = ''; return; }
    var dur = Math.max(80, Math.min(300, 300 * Math.abs(from) / Math.max(1, slideDistance())));
    el.style.transform = 'translateY(' + from + 'px)';
    void el.offsetHeight;
    el.style.transition = 'transform ' + Math.round(dur) + 'ms ease-out';
    el.style.transform = 'translateY(0px)';
    activeHead = el;
  }

  function beginShow() {
    if (settleTimer) { clearTimeout(settleTimer); settleTimer = null; }
    if (!state.returned) {
      // Pin swap between identical frames: shown pins + transforms holding the old spot.
      toggle('returned', RETURNED, true);   // nav pin: park → shown
      toggle('sheet', SHEET, true);         // strip pin: 0 → below the nav
      setTransforms(-slideDistance(), true);
    }
    toggle('anim', ANIM, true);
    setTransforms(0, false);
    settleTimer = setTimeout(settleShow, 400);
  }
  function settleShow() {
    settleTimer = null;
    if (wantShown) {
      clearTransforms(true);                // pins hold the geometry; release transforms
      clearHead();
      toggle('anim', ANIM, false);
    }
  }

  function beginHide() {
    if (settleTimer) { clearTimeout(settleTimer); settleTimer = null; }
    toggle('anim', ANIM, true);
    setTransforms(-slideDistance(), false); // both ride up together; sheet class stays
    settleTimer = setTimeout(settleHide, 400);
  }
  function settleHide() {
    settleTimer = null;
    if (!wantShown && state.returned) {
      // Both hidden/at-origin: swap pins back between identical frames. The glued title
      // ends at shownPin − slide, which should equal its collapsed pin; measure before
      // the swap and let bridgeHead ease any few-px residual rather than snapping.
      var p = prepHead();
      toggle('returned', RETURNED, false);
      toggle('sheet', SHEET, false);
      clearTransforms(true);
      bridgeHead(p);
      toggle('anim', ANIM, false);
    }
  }

  group.addEventListener('transitionend', function (e) {
    if (e.target !== group || e.propertyName !== 'transform') return;
    if (settleTimer) { clearTimeout(settleTimer); settleTimer = null; }
    if (wantShown) settleShow(); else settleHide();
  });

  function read() {
    ticking = false;
    var y = clampY();
    if (y === lastY) return;
    var up = y < lastY;
    if (up !== goingUp) {
      goingUp = up;
      if (up) upReversalY = lastY; else downReversalY = lastY;
    }
    lastY = y;

    var h = group.offsetHeight;
    /* The gate adapts to the page: on short pages (PDPs at some zooms) a fixed park
       depth can exceed the entire scroll range — the bar could leave but never be
       summoned back (James: "the bar has disappeared... just the logo"). Clamp the gate
       so at least SHOW_TRAVEL of summoning room always exists below the page's bottom. */
    var maxY = Math.max(document.documentElement.scrollHeight - window.innerHeight, 0);
    var gate = Math.min(h + PARK_BUFFER + 20,
                        Math.max(maxY - SHOW_TRAVEL - 60, varPx('--bokja-announce-height', 40) + 20));

    var next = wantShown;
    if (goingUp && y > gate && upReversalY - y >= SHOW_TRAVEL) next = true;
    if (!goingUp && y > gate && y - downReversalY >= HIDE_TRAVEL) next = false;

    if (y <= varPx('--bokja-announce-height', 40)) {
      // Pins and natural flow coincide here: drop everything with no motion.
      if (state.returned || state.sheet) {
        toggle('returned', RETURNED, false);
        toggle('sheet', SHEET, false);
        clearTransforms(true);
      }
      clearHead();
      toggle('anim', ANIM, false);
      next = false;
      wantShown = false;
    } else if (next !== wantShown) {
      // Capture the pinned title's rendered position BEFORE any class flips, flip
      // everything (collapsed included — the title's pin reads both classes), then
      // bridge the title's jump with a transform on the shared clock. One JS turn,
      // one paint: nothing unmasked ever reaches the screen.
      var headPrep = prepHead();
      wantShown = next;
      if (next) beginShow(); else beginHide();
      toggle('collapsed', COLLAPSED, y > h + 8 && !wantShown);
      bridgeHead(headPrep);
      return;
    }

    toggle('collapsed', COLLAPSED, y > h + 8 && !wantShown);

    /* Mid-ride handoff: while the sheet is travelling, fast scroll can pin a DIFFERENT
       group head — it arrives on the settled pin with no event. Catch it here (this
       runs once per scroll frame) and glue it to the sheet's live offset. */
    if (state.anim) handoffHead();
  }

  window.addEventListener('scroll', function () {
    if (ticking) return;
    ticking = true;
    window.requestAnimationFrame(read);
  }, { passive: true });

  /* Diagnostics chip (?hud=1): burns the live state into the corner so a screenshot IS
     the bug report — scrollY, classes, real vs var geometry, variant, UA. Preview-only
     tool; costs nothing unless the param is present. */
  try {
    if (new URLSearchParams(location.search).get('hud') === '1') {
      var hud = document.createElement('div');
      hud.style.cssText = 'position:fixed;inset-block-end:8px;inset-inline-start:8px;z-index:9999;background:#1a1a1a;color:#fff;font:10px/1.5 monospace;padding:6px 8px;pointer-events:none;white-space:pre;';
      document.body.appendChild(hud);
      var stripEl = strip;
      setInterval(function () {
        var g = group.getBoundingClientRect();
        var st = stripEl ? stripEl.getBoundingClientRect() : null;
        var cs = getComputedStyle(root);
        hud.textContent =
          'y=' + Math.round(window.scrollY) +
          ' cls=' + (state.collapsed ? 'C' : '') + (state.returned ? 'R' : '') + (state.sheet ? 'H' : '') + (state.anim ? 'A' : '') +
          ' variant=' + (root.getAttribute('data-collapse-variant') || 'classic') +
          '\nnavTop=' + Math.round(g.top) + ' navBot=' + Math.round(g.bottom) + ' navH=' + Math.round(g.height) +
          (st ? ' stripTop=' + Math.round(st.top) + ' stripPos=' + getComputedStyle(stripEl).position : ' strip=none') +
          '\nvarH=' + cs.getPropertyValue('--bokja-header-height').trim() + ' varA=' + cs.getPropertyValue('--bokja-announce-height').trim() +
          ' w=' + window.innerWidth + ' dpr=' + (window.devicePixelRatio || 1) +
          '\n' + navigator.userAgent.slice(-60);
      }, 250);
    }
  } catch (e) { /* hud is best-effort */ }

  // Deep-link arrivals: parked and collapsed past the header; never shown without a gesture.
  toggle('collapsed', COLLAPSED, lastY > group.offsetHeight + 8);

})();
